import type { FC } from "react";
import { useMemo } from "react";
import { Pressable } from "react-native";
import { Button, Card, GeneralDialog, plural, RenderMd } from "@ptsd/uikit";
import { Info } from "@tamagui/lucide-icons";
import { useTranslation } from "react-i18next";
import type { GetThemeValueForKey } from "tamagui";
import { Dialog, Text, YStack } from "tamagui";

import { api } from "~/utils/api";
import { scalesColorsByLevel } from "../scales";

interface Scale {
  code: string;
  name: string;
  value: number;
  percentage: number;
  level: string | undefined;
}

interface Score {
  text: string;
  color: GetThemeValueForKey<"color">;
}
interface PersonResearchDialogProps {
  scale: Scale;
}

export const PersonResearchDialog: FC<PersonResearchDialogProps> = ({
  scale,
}) => {
  const { t } = useTranslation("person");
  const score = useMemo<Score>(() => {
    return {
      color: scalesColorsByLevel(scale.level).color,
      text: plural(
        scale.percentage,
        t("dialog.scorePlural.one"),
        t("dialog.scorePlural.few"),
        t("dialog.scorePlural.many"),
      ),
    };
  }, [scale.percentage, t, scale.level]);

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Pressable>
          <Info size={20} color={score.color} />
        </Pressable>
      </Dialog.Trigger>
      <Dialog.Portal>
        <GeneralDialog.Overlay>
          <GeneralDialog.Content width={"100%"} maxWidth={1200}>
            <Card
              gap={"$5"}
              paddingVertical={"$5"}
              paddingHorizontal={"$4"}
              backgroundColor={"#EEEEEE"}
            >
              <ResearchDialogContent scale={scale} score={score} />
              <Dialog.Close asChild>
                <Button
                  sizes="lg"
                  rounded="lg"
                  variant="primary"
                  alignSelf="center"
                  minWidth={280}
                >
                  {t("dialog.close")}
                </Button>
              </Dialog.Close>
            </Card>
          </GeneralDialog.Content>
        </GeneralDialog.Overlay>
      </Dialog.Portal>
    </Dialog>
  );
};

interface ResearchDetailsProps {
  scale: Scale;
  score: Score;
}

const ResearchDialogContent: FC<ResearchDetailsProps> = ({ scale, score }) => {
  const { t } = useTranslation("person");
  const [data] = api.personal.scaleDetails.useSuspenseQuery({
    scaleCode: scale.code,
    score: scale.value,
  });

  return (
    <Card
      variant="borderless"
      backgroundColor={"white"}
      borderRadius={"$5"}
      gap={"$3"}
      maxHeight={"60vh"}
      style={{ overflowY: "auto" }}
    >
      <GeneralDialog.Title textAlign="left" fontSize={"$7"} lineHeight={"$7"}>
        {scale.name}
      </GeneralDialog.Title>

      {!!data?.description && (
        <YStack gap={"$2"} marginBottom={"$4"}>
          <Text
            fontFamily={"$body"}
            fontSize={"$5"}
            fontWeight={"$5"}
            color={"$gray-600"}
          >
            {t("dialog.scaleMeaning")}
          </Text>
          <RenderMd md={data.description} />
        </YStack>
      )}
      <YStack gap={"$2"}>
        <Text
          fontFamily={"$body"}
          fontSize={"$5"}
          fontWeight={"$5"}
          color={"$gray-600"}
        >
          {t("dialog.resultMeaning")}
        </Text>
        <Text
          fontFamily={"$body"}
          fontSize={"$6"}
          fontWeight={"$6"}
          color={score.color}
        >
          {`${scale.percentage} ${score.text}`}
        </Text>
        <Text
          fontFamily={"$body"}
          fontSize={"$4"}
          lineHeight={"$4"}
          fontWeight={"$5"}
        >
          {t("dialog.scoreDesc")}
        </Text>
        {!!data?.interpretation && (
          <RenderMd md={data?.interpretation.fullText} />
        )}
      </YStack>
    </Card>
  );
};
